/* Owns the grouped navigation of PANEL.md §4: 240 px, collapsible to 56 px
   icons with the state in localStorage['sac.sidebar'], an off-canvas Drawer
   below `lg`, and per-item permission filtering. An item the role cannot read is
   omitted entirely — that is the one place the panel hides rather than disables,
   because a nav entry that always 403s is noise, not information. */

import {
  Map,
  Activity,
  ChevronLeft,
  ChevronRight,
  Gavel,
  Globe,
  Image,
  KeyRound,
  LayoutDashboard,
  Search,
  Server,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Terminal,
  UserCog,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router'

import { cn } from '../components/ui/cn.js'
import { Drawer } from '../components/ui/Drawer.js'
import { useT, type TFunction } from '../i18n/index.js'
import { canRole, type Permission, type Role } from '../lib/permissions.js'

export interface NavItem {
  /** i18n key, also the palette's match target. */
  labelKey: string
  /** Path relative to `/s/:serverId`; the caller prefixes it. */
  path: string
  icon: LucideIcon
  permission: Permission
}

export interface NavGroup {
  labelKey: string
  items: NavItem[]
}

/** The §4 table verbatim — order, icons and permissions are all spec-fixed. */
export const SERVER_NAV_GROUPS: readonly NavGroup[] = [
  {
    labelKey: 'nav.groupGeneral',
    items: [
      { labelKey: 'nav.dashboard', path: 'dashboard', icon: LayoutDashboard, permission: 'server.read' },
      { labelKey: 'nav.live', path: 'live', icon: Activity, permission: 'detections.read' },
      { labelKey: 'nav.map', path: 'map', icon: Map, permission: 'players.read' },
    ],
  },
  {
    labelKey: 'nav.groupPlayers',
    items: [
      { labelKey: 'nav.players', path: 'players', icon: Users, permission: 'players.read' },
      { labelKey: 'nav.detections', path: 'detections', icon: ShieldAlert, permission: 'detections.read' },
      { labelKey: 'nav.bans', path: 'bans', icon: Gavel, permission: 'bans.read' },
      { labelKey: 'nav.globalBans', path: 'global-bans', icon: Globe, permission: 'server.read' },
      { labelKey: 'nav.whitelist', path: 'whitelist', icon: ShieldCheck, permission: 'server.read' },
      { labelKey: 'nav.screenshots', path: 'screenshots', icon: Image, permission: 'screenshots.read' },
    ],
  },
  {
    labelKey: 'nav.groupManage',
    items: [
      { labelKey: 'nav.config', path: 'config', icon: SlidersHorizontal, permission: 'config.read' },
      { labelKey: 'nav.commands', path: 'commands', icon: Terminal, permission: 'server.read' },
      { labelKey: 'nav.discord', path: 'discord', icon: Webhook, permission: 'server.read' },
      { labelKey: 'nav.team', path: 'team', icon: UserCog, permission: 'server.read' },
      { labelKey: 'nav.licenses', path: 'licenses', icon: KeyRound, permission: 'license.manage' },
      { labelKey: 'nav.audit', path: 'audit', icon: ScrollText, permission: 'audit.read' },
      { labelKey: 'nav.settings', path: 'settings', icon: Settings, permission: 'server.read' },
    ],
  },
]

/** Reachable without a selected server, so they stay visible on `/servers` and `/account`. */
const GLOBAL_NAV: ReadonlyArray<{ labelKey: string; to: string; icon: LucideIcon }> = [
  { labelKey: 'nav.servers', to: '/servers', icon: Server },
  { labelKey: 'nav.lookup', to: '/lookup', icon: Search },
]

/** Every server route the caller may open, in sidebar order — the palette's `nav.pages` source. */
export function visibleServerNav(role: Role | null, serverId: string): Array<NavItem & { to: string }> {
  const result: Array<NavItem & { to: string }> = []
  for (const group of SERVER_NAV_GROUPS) {
    for (const item of group.items) {
      if (canRole(role, item.permission)) result.push({ ...item, to: `/s/${serverId}/${item.path}` })
    }
  }
  return result
}

const STORAGE_KEY = 'sac.sidebar'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'collapsed'
  } catch {
    // A storage-blocked browser still gets a working sidebar, just not a sticky one.
    return false
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, collapsed ? 'collapsed' : 'expanded')
  } catch {
    /* Losing the preference is acceptable; failing to collapse is not. */
  }
}

const linkClass = (isActive: boolean, collapsed: boolean): string =>
  cn(
    'flex min-h-11 items-center gap-3 rounded-md px-3 text-sm transition-colors md:min-h-9',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    collapsed && 'justify-center px-0',
    isActive ? 'bg-elevated text-text' : 'text-text-muted hover:bg-elevated hover:text-text',
  )

interface NavBodyProps {
  t: TFunction
  serverId: string | null
  role: Role | null
  collapsed: boolean
  onNavigate?: () => void
}

function NavBody({ t, serverId, role, collapsed, onNavigate }: NavBodyProps): ReactNode {
  return (
    <nav aria-label={t('nav.mainNav')} className="flex flex-col gap-4 px-2 py-3">
      <ul className="flex flex-col gap-1">
        {GLOBAL_NAV.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              onClick={onNavigate}
              title={collapsed ? t(item.labelKey) : undefined}
              className={({ isActive }) => linkClass(isActive, collapsed)}
            >
              <item.icon aria-hidden className="size-4 shrink-0" />
              <span className={cn(collapsed && 'sr-only')}>{t(item.labelKey)}</span>
            </NavLink>
          </li>
        ))}
      </ul>

      {serverId
        ? SERVER_NAV_GROUPS.map((group) => {
            const items = group.items.filter((item) => canRole(role, item.permission))
            if (items.length === 0) return null
            return (
              <div key={group.labelKey} className="flex flex-col gap-1">
                <h2
                  className={cn(
                    'px-3 text-xs font-medium tracking-wide text-text-faint uppercase',
                    collapsed && 'sr-only',
                  )}
                >
                  {t(group.labelKey)}
                </h2>
                <ul className="flex flex-col gap-1">
                  {items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={`/s/${serverId}/${item.path}`}
                        onClick={onNavigate}
                        title={collapsed ? t(item.labelKey) : undefined}
                        className={({ isActive }) => linkClass(isActive, collapsed)}
                      >
                        <item.icon aria-hidden className="size-4 shrink-0" />
                        <span className={cn(collapsed && 'sr-only')}>{t(item.labelKey)}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })
        : null}
    </nav>
  )
}

export interface SidebarProps {
  serverId: string | null
  role: Role | null
  /** Below `lg` the sidebar is an off-canvas Drawer driven by the Topbar hamburger. */
  mobileOpen: boolean
  onMobileOpenChange: (open: boolean) => void
}

export function Sidebar({ serverId, role, mobileOpen, onMobileOpenChange }: SidebarProps): ReactNode {
  const t = useT()
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed)

  useEffect(() => {
    writeCollapsed(collapsed)
  }, [collapsed])

  const closeMobile = useCallback(() => onMobileOpenChange(false), [onMobileOpenChange])

  return (
    <>
      <aside
        data-testid="sidebar"
        aria-label={t('nav.sidebar')}
        className={cn(
          'hidden shrink-0 flex-col border-r border-border bg-panel lg:flex',
          collapsed ? 'w-14' : 'w-60',
        )}
      >
        <div className="flex-1 overflow-y-auto">
          <NavBody t={t} serverId={serverId} role={role} collapsed={collapsed} />
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-expanded={!collapsed}
          className="flex min-h-9 items-center justify-center gap-2 border-t border-border px-3 text-xs text-text-faint hover:bg-elevated hover:text-text focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
        >
          {collapsed ? <ChevronRight aria-hidden className="size-4" /> : <ChevronLeft aria-hidden className="size-4" />}
          <span className={cn(collapsed && 'sr-only')}>{collapsed ? t('common.expand') : t('common.collapse')}</span>
        </button>
      </aside>

      <Drawer
        open={mobileOpen}
        onOpenChange={onMobileOpenChange}
        side="bottom"
        title={t('nav.sidebar')}
        data-testid="sidebar-drawer"
      >
        <NavBody t={t} serverId={serverId} role={role} collapsed={false} onNavigate={closeMobile} />
      </Drawer>
    </>
  )
}
